import { useEffect, useState } from "react";
import {
  customerOrgAndCompAutocomplete,
  customerOrgAutocomplete,
} from "../../../../../features/customer/customerAPI";

export interface CustomerOption {
  id: string;
  name: string;
  companyName?: string;
  customerCompanyId?: string;
  salesPermission?: {
    riskLevel: string;
    verificationDate: string;
  };
}

type Props = {
  includeCompanies?: boolean;
};

const useCustomerAutocomplete = ({ includeCompanies = false }: Props) => {
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);

  useEffect(() => {
    if (customerSearchTerm) {
      const fetchFunction = includeCompanies
        ? customerOrgAndCompAutocomplete
        : customerOrgAutocomplete;

      fetchFunction({ name: customerSearchTerm }).then((res) => {
        let options: CustomerOption[];

        if (includeCompanies) {
          // Flatten nested structure: companies contain customerOrganizations array
          options = res.flatMap((company: any) =>
            company.customerOrganizations.map((org: any) => ({
              id: org.id,
              name: org.name,
              companyName: company.name,
              customerCompanyId: company.id,
              salesPermission: org.salesPermission,
            })),
          );

          // Sort by company name first, then by org name
          options.sort((a, b) => {
            const companyCompare = (a.companyName || "").localeCompare(
              b.companyName || "",
              "fi",
            );
            if (companyCompare !== 0) return companyCompare;
            return a.name.localeCompare(b.name, "fi");
          });
        } else {
          // Map to ensure consistent structure
          options = res
            .map((org: any) => ({
              id: org.id,
              name: org.name,
              customerCompanyId: org.customerCompanyId,
              salesPermission: org.salesPermission,
            }))
            .sort((a: CustomerOption, b: CustomerOption) =>
              a.name.localeCompare(b.name, "fi"),
            );
        }

        setCustomerOptions(options);
      });
    } else {
      setCustomerOptions([]);
    }
  }, [customerSearchTerm, includeCompanies]);

  return {
    setCustomerSearchTerm,
    customerOptions,
  };
};

export default useCustomerAutocomplete;
